import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="31724" className="hidden lg:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="font-sans text-base font-medium text-foreground/90 hover:text-primary transition-colors bg-transparent hover:bg-transparent focus:bg-transparent relative after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-md lg:hidden animate-in fade-in slide-in-from-top-5 duration-200">
      <div className="container mx-auto px-4 py-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-8">
          <Link to="/HomePage" className="block" onClick={onClose}>
            <img src="https://solutionsforsleepapnea.com/wp-content/uploads/2026/04/523944_1670889061_Color-scaled.png" alt="Solutions for Sleep Apnea" className="h-8 w-auto object-contain" />
          </Link>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu">
            <X className="h-6 w-6 text-primary" />
          </Button>
        </div>

        <nav className="flex-1 flex flex-col gap-6 overflow-y-auto">
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3].map(i => <div key={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" data-index={i} />)}
            </div> : <ul className="flex flex-col gap-4">
              {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="text-xl font-sans font-semibold text-foreground hover:text-primary transition-colors block py-2" onClick={onClose}>
                    {item.label}
                  </Link>
                </li>)}
            </ul>}
          <div className="mt-auto pt-8 border-t border-border">
            <Button asChild className="w-full text-lg h-14 bg-accent hover:bg-accent/90 text-white shadow-lg" size="lg">
              <Link to="#" onClick={onClose}>
                Get a Free Consultation
              </Link>
            </Button>
            <div className="mt-6 text-center text-muted-foreground font-sans text-sm">
              <p>123 Sleep Wellness Way, Medical District</p>
              <p className="mt-2 font-semibold text-primary">(800) 555-7533</p>
            </div>
          </div>
        </nav>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={cn("sticky top-0 z-40 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/90 backdrop-blur-lg border-border shadow-md py-2" : "bg-background border-transparent py-4")}>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block" aria-label="Solutions for Sleep Apnea Home">
            <img src="https://solutionsforsleepapnea.com/wp-content/uploads/2026/04/523944_1670889061_Color-scaled.png" alt="Solutions for Sleep Apnea" className="h-12 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="16" className="header-menu">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-6">
          <Link to="tel:8005557533" className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
            <Phone className="h-4 w-4" />
            <span>(310) 858-7373</span>
          </Link>
          <Button asChild className="font-semibold bg-accent hover:bg-accent/90 text-white shadow-md hover:shadow-lg transition-all rounded-lg px-6 h-11">
            <Link to="#">Get a Free Consultation</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" className="text-primary hover:bg-secondary">
            <Menu className="h-7 w-7" />
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16" className="mobile-menu-provider">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}